/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { getCatalogId, ICoreContext, msdyn365Commerce } from '@msdyn365-commerce/core-internal';
import { AttributeValue, CommerceProperty, ProductCatalog, ProductDeliveryOptions, ProductDimensionValue, ProductDimensionValueInventoryAvailability, ProductPrice, ProductType, ProjectionDomain, ReleasedProductType, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { BulkPurchase } from '@msdyn365-commerce-modules/bulk-purchase';
import {
    ArrayExtensions, DeliveryMode, FinitePromiseQueue, FinitePromiseQueueError,
    getDeliveryOptionsForSelectedVariant, GetDeliveryOptionsForSelectedVariantInput,
    getDimensionsForSelectedVariant, GetDimensionsForSelectedVariantInput, getDimensionValuesFromQuery, getInventoryLevelCodeFromDimensionValue,
    getPriceForSelectedVariant, getProductAvailabilitiesForSelectedVariant, getSelectedVariant, IDimensionsApp, IDimensionValueForSelectedVariant,
    InventoryLevelValues, IProductInventoryInformation, IPromiseQueue, PriceForSelectedVariantInput,
    ProductAvailabilitiesForSelectedVariantInput, QueryResultSettingsProxy, SelectedVariantInput, setDimensionValuesToQuery,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import { Button, getTelemetryObject, getPayloadObject, IModuleProps, INodeProps, getTelemetryAttributes, onTelemetryClick, ITelemetryContent, TelemetryConstant, updateMaxQuantityForCartLineItem } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import * as MsDyn365 from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';

import {
    getBuyboxAddToCart, getBuyBoxInventoryLabel, getBuyboxKeyInPrice,
    getBuyboxProductAddToOrderTemplate, //getBuyboxProductAddToWishlist,
    getBuyboxProductDescription, getBuyboxProductPrice, getBuyboxProductQuantity,
    getBuyboxProductRating, getBuyboxProductTitle, getBuyboxProductUnitOfMeasure,
    getBuyboxShopSimilarButton, getQuantityLimitsMessages,
    RetailDefaultOrderQuantityLimitsFeatureName, IAlternateItems,
    getProductWarehouseDetails,
    getBuyboxProductShortDescription
} from './components/buybox-components';
import { getBuyboxProductConfigure, getBuyboxProductComparisonButton, EcommerceQuantityLimitConfigurationsFeatureName, RevertToSiteBuilderOrderQuantityLimitsSettingsCRTFeatureName } from '@msdyn365-commerce-modules/buybox';
import {
    IBuyboxAddToCartViewProps, IBuyboxAddToOrderTemplateViewProps, IBuyboxAddToWishlistViewProps,
    IBuyboxCallbacks, IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureViewProps, IBuyboxProductQuantityViewProps, IBuyboxShopSimilarLookViewProps,
    IBuyboxState, IErrorState, ShopSimiliarButtonType, RecentlyViewedState
} from '@msdyn365-commerce-modules/buybox';
import { IBuyboxExtentedProps, IBuyboxCommonData, IAlternateProduct } from '../../themes/adventureworks/views/common/buyboxInterface';
import { IBuyboxData } from './buybox.data';
import { IBuyboxProps, IBuyboxResources } from './buybox.props.autogenerated';
import { getBuyboxFindInStore, IBuyboxFindInStoreViewProps } from './components/buybox-find-in-store';
import { getActivePricesAsync, getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

export interface IBuyboxCustomState extends IBuyboxState {
    confirmationHeaderText?: string;
    confirmationBodyText?: string;
    isItemSupersede: boolean;
    isSoftSupresed: boolean;
    isLimitedBackOrdered: boolean;
    isBackOrdered: boolean;
    isPriceVisible: boolean;
}

export interface IBuyboxCustomCallbacks extends IBuyboxCallbacks {
    updateConfirmPopupMessage(confirmHeaderText: string, confirmBodyText: string, isItemSupersede: boolean): void;
    updateSoftSuppresedFlag(isSoftSupresede: boolean): void;
    updateBackOrdered(isBackOrdered: boolean): void;
    updateLimitedBackOrder(isLimitedBackOrdered: boolean): void;
}

export interface IBuyboxViewProps extends IBuyboxProps<IBuyboxData> {
    state: IBuyboxCustomState;
    ModuleProps: IModuleProps;
    ProductInfoContainerProps: INodeProps;
    MediaGalleryContainerProps: INodeProps;
    callbacks: IBuyboxCustomCallbacks;
    mediaGallery?: React.ReactNode;
    title?: React.ReactNode;
    shortDescription?: React.ReactNode;
    description?: React.ReactNode;
    rating?: React.ReactNode;
    price?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    bulkPurchaseLink?: React.ReactNode;
    addToOrderTemplate?: IBuyboxAddToOrderTemplateViewProps;
    addToWishlist?: IBuyboxAddToWishlistViewProps;
    min?: number | undefined;
    max: number | undefined;
    addToCart: IBuyboxAddToCartViewProps;
    findInStore?: IBuyboxFindInStoreViewProps;
    quantity?: IBuyboxProductQuantityViewProps;
    configure?: IBuyboxProductConfigureViewProps;
    inventoryLabel?: React.ReactNode;
    shopSimilarLook?: IBuyboxShopSimilarLookViewProps;
    shopSimilarDescription?: IBuyboxShopSimilarLookViewProps;
    quantityLimitsMessages: React.ReactNode;
    telemetryContent?: ITelemetryContent;
    keyInPrice?: IBuyboxKeyInPriceViewProps;
    unitOfMeasure?: React.ReactNode;
    priceState?: Boolean;
    signInLink?: React.ReactNode;
    OverSizedLabel?: React.ReactNode;
    invetLabel?: String;
    etaLabel?: String[];
    alternateItem?: IAlternateItems;
    popupElm?: any;
    productWarehouseDtls?: React.ReactNode;
    relatedProducts?: React.ReactNode;
    productDetailsTab?:React.ReactNode;
    IsActive?: boolean;
    IsAlternateItemCallFailed?: React.ReactNode;
    catalogs?: ProductCatalog[] | undefined;
    isChannelMultipleCatalogsFeatureEnabled?: boolean;
    currentCatalogId?: number;
    applyDefaultOrderSettings?: boolean;
}

/**
 * Buybox Module.
 */
class Buybox extends React.PureComponent<IBuyboxProps<IBuyboxData>, IBuyboxCustomState> {
    /**
     * A queue of tasks of processing the changes in the dimensions.
     * Limit to two processes:
     * 1 - for the current process, which is under execution at the moment.
     * 2 - next process, which will process the latest version of data.
     * @remark Enqueueing new promises will discard the previous ones (except the one which is under processing).
     */
    private readonly dimensionUpdateQueue: IPromiseQueue<void> = new FinitePromiseQueue<void>(2);

    private dimensions: { [id: number]: string } = {};
    private initializedDimensions: Set<string> = new Set();
    private alternateProdAttributes: AttributeValue[] | undefined;

    private alternateProdPrice: ProductPrice[] | undefined;

    private readonly buyboxCallbacks: IBuyboxCustomCallbacks = {
        updateQuantity: (newQuantity: number): boolean => {
            const errorState = { ...this.state.errorState };
            errorState.quantityError = undefined;
            errorState.otherError = undefined;

            this.setState({ quantity: newQuantity, errorState });
            return true;
        },
        updateErrorState: (newErrorState: IErrorState): void => {
            this.setState({ errorState: newErrorState });
        },
        updateSelectedProduct: (
            newSelectedProduct: Promise<SimpleProduct | null>,
            newInventory: IProductInventoryInformation | undefined,
            newPrice: ProductPrice | undefined,
            newDeliveryOptions: ProductDeliveryOptions | undefined
        ): void => {
            this.setState({
                selectedProduct: newSelectedProduct,
                productAvailableQuantity: newInventory,
                productDeliveryOptions: newDeliveryOptions
            });
            this._updatePrice(newPrice);
        },
         initializeDimension: (initializedDimension: string): void => {
            if (initializedDimension) {
                this.initializedDimensions.add(initializedDimension);
            }
        },
        dimensionSelectedAsync: async (selectedDimensionId: number, selectedDimensionValueId: string): Promise<void> => {
            this.dimensions[`${selectedDimensionId}`] = selectedDimensionValueId;
            return this.dimensionUpdateQueue.enqueue(async () => {
                return this._updateDimensions();
            }).catch((error: any) => { // Ignore discarded processes.
                if (error !== FinitePromiseQueueError.ProcessWasDiscardedFromTheQueue) {
                    throw error;
                }
            });
        },
        getDropdownName: (dimensionType: number, resources: IBuyboxResources): string => {
            return this._getDropdownName(dimensionType, resources);
        },
        changeModalOpen: (isModalOpen: boolean): void => {
            this.setState({ modalOpen: isModalOpen });
        },
        changeUpdatingDimension: (isUpdatingDimension: boolean): void => {
            this.setState({ isUpdatingDimension });
        },

        /**
         * Update isUpdatingDeliveryOptions state.
         *
         * @param isUpdatingDeliveryOptions - The status of updating delivery options.
         */
        changeUpdatingDeliveryOptions: (isUpdatingDeliveryOptions: boolean): void => {
            this.setState({ isUpdatingDeliveryOptions });
        },

        updateKeyInPrice: (customPrice: number): void => {
            // Remove custom amount error when updating the custom price
            const errorState = { ...this.state.errorState };
            errorState.customAmountError = undefined;

            this.setState({ isPriceKeyedIn: true, keyInPriceAmount: customPrice, errorState });
            this._updatePrice(this.state.productPrice, customPrice);
        },
        updateConfirmPopupMessage: (headerText: string, bodyText: string, isItemSupersede: boolean): void => {
            this.setState({ confirmationHeaderText: headerText, confirmationBodyText: bodyText, isItemSupersede: isItemSupersede });
        },
        updateSoftSuppresedFlag: (isSoftSupresede: boolean): void => {
            this.setState({ isSoftSupresed: isSoftSupresede });
        },
        updateBackOrdered: (isBackOrdered): void => {
            this.setState({ isBackOrdered: isBackOrdered });
        },
        updateLimitedBackOrder: (isLimitedBackOrdered): void => {
            this.setState({ isLimitedBackOrdered: isLimitedBackOrdered });
        }
    };

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBuyboxProps<IBuyboxData>, state: IBuyboxCustomState) {
        super(props);
        this.state = {
            errorState: {
                configureErrors: {}
            },
            quantity: 1,
            min: undefined,
            max: undefined,
            selectedProduct: undefined,
            productPrice: undefined,
            productDeliveryOptions: undefined,
            modalOpen: false,
            isUpdatingDimension: false,
            isUpdatingDeliveryOptions: false,
            isPriceApiCalled: false,
            confirmationBodyText: undefined,
            confirmationHeaderText: undefined,
            isItemSupersede: false,
            isSoftSupresed: false,
            isLimitedBackOrdered: false,
            isBackOrdered: false,
            isPriceVisible: false
        };
        this.telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    }

    // public shouldComponentUpdate(nextProps: Readonly<IBuyboxProps<IBuyboxData>>, nextState: Readonly<IBuyboxCustomState>, nextContext: any): boolean{
    //     const prodRecId:number[] =[];

    //     if(this.props && this.props.data && this.props.data.product)
    //     {
    //         this.props.data.product.then((product)=>{
    //             prodRecId.push(product.RecordId);
    //             getProductsSalesAgreementAsync({callerContext: this.props.context.actionContext},prodRecId)
    //             .then((tradeAgreements)=>{
    //                 this.setState({productTradeAgreement: tradeAgreements});
    //             });
    //         });
    //     }
    //     return false;
    // }

    public async componentDidMount(): Promise<void> {
        const {
            data: {
                product: { result: product },
                productPrice,
                alternateItems:{result: alternateItem}
            },
            context,
            context: {
                request: {
                    apiSettings: { channelId, catalogId }
                }
            }
        } = this.props;

        void productPrice.then(async (result) => {
            this._updatePrice(result);
        });

           // There are multiple sources triggering the callback function dimensionSelectedAsync calling the GetActivePrice api before componentDidMount and after componentDidMount.
        // Set isPriceApiCalled to false here to prevent redundant API call after componentDidMount:
        // If the price has not been hydrated before, this means the component was just loaded for the first time with variant selected.
        // We will skip calling RS API in this case as the price data has already been fetched via module data action in node server.
          const matchedDimensions = getDimensionValuesFromQuery(this.props.context.request.url.requestUrl);

        // Set isPriceApiCalled to false only when variant is there
        if (matchedDimensions.length > 0) {
            this.setState({ isPriceApiCalled: false });
        } else {
            this.setState({ isPriceApiCalled: true });
        }

        productPrice.then(async result => {
            this._updatePrice(result);
        });

        if (product) {
            // Check if the product is service or not by product type
            if (product.ItemTypeValue === ReleasedProductType.Service) {
                this.setState({ isServiceItem: true });
            }
            await this._updateQuantitiesInState(product);
        }
        if (product && context.app.config.maxRecentlyViewedItemsCount > 0) {
            RecentlyViewedState.instance(this.props.context, context.app.config.maxRecentlyViewedItemsCount, product.RecordId);
        }

        if(alternateItem && alternateItem.respObj && alternateItem.respObj.product)
        {
            const alternateProd : IAlternateProduct = alternateItem.respObj.product;
            if(alternateProd.productDtl)
            {
                const productAttirbutes = await getAttributeValuesAsync( {
                    callerContext: this.props.context.actionContext,
                    queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(this.props.context.actionContext)
                },
                alternateProd.productDtl.RecordId,
                channelId,
                catalogId);
                if(typeof productAttirbutes !== 'undefined' && productAttirbutes !== null)
                {
                    this.alternateProdAttributes = productAttirbutes;
                }
                const projectDomain: ProjectionDomain = {
                    ChannelId: +this.props.context.actionContext.requestContext.apiSettings.channelId,
                    CatalogId: this.props.context.actionContext.requestContext.apiSettings.catalogId
                };
                const prodIds:number[]=[];
                prodIds.push(alternateProd.productDtl.RecordId);
                const altProductPrice =  await getActivePricesAsync({ callerContext: this.props.context.actionContext, bypassCache: 'none' },projectDomain,prodIds,new Date(),this.props.data.customerInformation?.result?.AccountNumber,undefined,true,this.props.data.featureState.result?.find(
                    featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
                )?.IsEnabled && (alternateProd.productDtl.ProductTypeValue === ProductType.KitMaster || alternateProd.productDtl.ProductTypeValue === ProductType.Master),this.props.data.featureState.result?.find(
                    featureState => featureState.Name === 'Dynamics.AX.Application.RetailSalesAgreementPriceSupportFeature'
                )?.IsEnabled);
                if(altProductPrice)
                {
                    this.alternateProdPrice = altProductPrice;
                }
            }
        }
    }

    private _getSigninButton(customer: Customer | undefined, user: MsDyn365.IRequestContextUser | undefined, resources: IBuyboxResources): React.ReactNode | undefined {
        const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignIn);
        const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
        return !customer && (
            <Button
                className='msc-add-to-cart '
                title={resources.loginToBuyText}
                href={user && user.signInUrl}
                aria-label={resources.loginToBuyText}
                onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Sign in')}
                {...attributes}
            >
                <span className='ms-header__signin-button-text' aria-hidden>
                    {resources.loginToBuyText}
                </span>
            </Button>
        );
    }

    // private _getRequestanAccountButton(customer: Customer | undefined, user: MsDyn365.IRequestContextUser | undefined, resources: IBuyboxResources): React.ReactNode | undefined {
    //  const payLoad = getPayloadObject('click', this.telemetryContent, TelemetryConstant.SignUp);
    //     const attributes = getTelemetryAttributes(this.telemetryContent, payLoad);
    //     return !customer && (
    //         <Button
    //             className='ms-buybox__find-in-store-button'
    //             title={resources.requestAnAccountText}
    //             href='/business-sign-up'
    //             aria-label={resources.requestAnAccountText}
    //             target="_blank"
    //             onClick={onTelemetryClick(this.telemetryContent, payLoad, 'Sign up')}
    //             {...attributes}
    //         >
    //             <span className='ms-header__signup-button-text' aria-hidden>
    //                 {resources.requestAnAccountText}
    //             </span>
    //         </Button>
    //     );
    // }

    public render(): JSX.Element | null {
        const {
            slots: {
                mediaGallery, productComparisonButton, relatedProducts, productSpecifications
            },
            data: {
                product: { result: product }
            },
            context: {
                request: {
                    user
                }
            },
            config: { className = '' }
        } = this.props;

        const
            {
                min, max
            } = this.state;

        if (!product) {
            this.props.context.telemetry.error('Product content is empty, module wont render');
            return null;
        }
        const props = this.props as IBuyboxExtentedProps<IBuyboxCommonData>;
        const defaultMinimumKeyInPrice = 10;
        const defaultMaximumKeyInPrice = 100;

        const context = this.props.context as ICoreContext<IDimensionsApp>;
        const inventoryLevel = context.app.config.inventoryLevel;
        const dimensionValuesWithInventory = ArrayExtensions.flatten(
            ArrayExtensions.validValues(this.props.data.productDimensions.result?.map(value => value.dimensionValuesWithInventory)));
             const applyDefaultOrderSettings =
            (this.props.data.featureState?.result?.find(featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName)
                ?.IsEnabled ??
                false) &&
            (this.props.data.cartConfiguration?.result?.ValidateDefaultOrderSettingsPerLine ?? false);

            const hasAvailableProducts =
            !ArrayExtensions.hasElements(dimensionValuesWithInventory) ||
            dimensionValuesWithInventory.some((value: unknown) => {
                const inventoryLevelCode = getInventoryLevelCodeFromDimensionValue(
                    value as ProductDimensionValueInventoryAvailability,
                    inventoryLevel
                );
                return inventoryLevelCode !== InventoryLevelValues.outOfStock;
            });
  const catalogId = getCatalogId(this.props.context.request);
        validateCatalogId(catalogId);

        let customerInfo;
        try {
            customerInfo = this.props.data.customerInformation?.result;
        } catch (error) {
            this.props.telemetry.debug('Unable to receive Customer Information. May be user is not authorized');
        }
        let isOversized: boolean | undefined = false;
        if (this.props.data.productSpecificationData.result) {
            this.props.telemetry.error('Product specification content is empty, module wont render.');
            // return null;
            this.props.data.productSpecificationData.result.map((specData, idx) => {

                if (specData.Name === 'Oversized') {
                    isOversized = specData.BooleanValue ? true : false;
                }
            });
        }
        let isOversizedAlternateItem: boolean | undefined = false;
        if (this.alternateProdAttributes) {
            this.alternateProdAttributes.map((specData, idx) => {
                if (specData.Name === 'Oversized') {
                    isOversizedAlternateItem = specData.BooleanValue ? true : false;
                }
            });
        }
        // console.log(isOversizedAlternateItem);
        const etaResult: String[] = new Array();
        const { etaData, alternateItems } = this.props.data;
        if (typeof etaData !== 'undefined' && typeof etaData.result !== 'undefined' ) {
            if(typeof etaData.result.etaObj !== 'undefined' &&(typeof etaData.result.etaObj.$id !== 'undefined' || typeof etaData.result.etaObj.ETADate !== 'undefined')){
                if (etaData.result.etaObj.ETADate) {
                    const dt = new Date(etaData.result.etaObj.ETADate);
                    etaResult.push(`${dt.toLocaleDateString()}`);
                }
                etaResult.push(etaData.result.etaObj.ResultMessage);
         }
        }
        let currentItemAlternate: IAlternateItems | undefined;
        let alternateProd: IAlternateProduct | undefined;
        if (alternateItems && alternateItems.result && alternateItems.result.respObj && alternateItems.result.respObj.response) {
            if(alternateItems.result.respObj.response.ItemId === product.ItemId) {
                currentItemAlternate = alternateItems.result.respObj.response;
            }
            alternateProd = alternateItems.result.respObj.product;
        }
        const inventLabel: string | undefined = this._getInventoryLabel(props.data.productAvailableQuantity.result);
        const addtoCartProps: IBuyboxExtentedProps<IBuyboxCommonData> = {
            resources: props.resources,
            config: props.config,
            slots: props.slots,
            app: props.app,
            context: props.context,
            telemetry: props.telemetry,
            renderModuleAttributes: props.renderModuleAttributes,
            renderView: props.renderView,
            data: props.data,
            dataActions: props.dataActions,
            friendlyName: props.friendlyName,
            id: props.id,
            typeName: props.typeName,
            inventoryLabel: inventLabel
        };
        let isActive = true;
        if(this.props.data.product && this.props.data.product.result && this.props.data.product.result.ExtensionProperties)
        {
            const isActiveProps = this.props.data.product.result.ExtensionProperties.filter(x=> x.Key === 'IsActive');
            if(isActiveProps && isActiveProps[0] && isActiveProps[0].Value !== undefined)
            {
                isActive= isActiveProps[0].Value.BooleanValue !== undefined ? isActiveProps[0].Value.BooleanValue : true;
            }
        }
        let isAlternateItemAvailable = false;
        if(currentItemAlternate && (currentItemAlternate.UseAltItemId === 'InventWhen0' || currentItemAlternate.UseAltItemId === 'Always') && alternateProd && alternateProd.productAvailibility)
        {
            isAlternateItemAvailable = true;
        }
        let isPriceAvailable = false;
        if(this.props.data && this.props.data.productTradeAgreements && this.props.data.productTradeAgreements.result && this.props.data.productTradeAgreements.result.length > 0)
        {
            if(this.props.data.productTradeAgreements.result[0] !== undefined && this.props.data.productTradeAgreements.result[0].TradeAgreementPrice !== 0)
            {
                isPriceAvailable = true;
            }
        }

        this.setState({isPriceVisible: isPriceAvailable && isActive ? true : false});

        let isAlternateItemCallFailed=false;
        if(!alternateItems || (alternateItems && !alternateItems.result) || (alternateItems && alternateItems.result && !alternateItems.result.respObj) || (alternateItems && alternateItems.result && alternateItems.result.respObj && !alternateItems.result.respObj.response))
        {
            isAlternateItemCallFailed = true;
        }

        const viewProps: IBuyboxViewProps = {
            ...(this.props as IBuyboxProps<IBuyboxData>),
            state: this.state,
            mediaGallery: mediaGallery && mediaGallery.length > 0 ? mediaGallery[0] : undefined,
            ModuleProps: {
                moduleProps: this.props,
                className: classnames('ms-buybox-content', className)
            },
            ProductInfoContainerProps: {
                className: 'ms-buybox__content'
            },
            MediaGalleryContainerProps: {
                className: 'ms-buybox__media-gallery'
            },
            relatedProducts: relatedProducts && relatedProducts.length > 0 ? relatedProducts[0]: undefined,
            telemetryContent: this.telemetryContent,
            callbacks: this.buyboxCallbacks,
            title: getBuyboxProductTitle(props),
            shortDescription: getBuyboxProductShortDescription(props),
            description: getBuyboxProductDescription(props),
            bulkPurchaseLink: this._renderBuyboxBulkPurchaseLink(),
            unitOfMeasure: getBuyboxProductUnitOfMeasure(props),
            configure: getBuyboxProductConfigure(props, this.state, this.buyboxCallbacks),
            findInStore: isActive && isPriceAvailable? getBuyboxFindInStore(this.props, this.state, this.buyboxCallbacks) : undefined,
            price: customerInfo ? getBuyboxProductPrice(props, this.state) : undefined,
            addToCart: getBuyboxAddToCart(
                addtoCartProps, this.state, this.buyboxCallbacks, defaultMinimumKeyInPrice, defaultMaximumKeyInPrice,isAlternateItemAvailable, undefined, hasAvailableProducts, currentItemAlternate, alternateProd, customerInfo, isActive,isOversized,isOversizedAlternateItem, isPriceAvailable, this.alternateProdPrice),
            productComparisonButton: ArrayExtensions.hasElements(productComparisonButton)
                ? getBuyboxProductComparisonButton(
                    productComparisonButton[0],
                    product,
                    getCatalogId(this.props.context.request),
                    this.state
                )
                : undefined,
            addToOrderTemplate: isActive && isPriceAvailable && customerInfo ? getBuyboxProductAddToOrderTemplate(props, this.state, this.buyboxCallbacks, isPriceAvailable && isActive ? true : false) : undefined,
            //addToWishlist: customerInfo ? getBuyboxProductAddToWishlist(props, this.state, this.buyboxCallbacks) : undefined,
            rating: !props.context.app.config.hideRating && getBuyboxProductRating(props),
            quantity: isActive && isPriceAvailable && customerInfo ? (product.IsGiftCard ? undefined : getBuyboxProductQuantity(props, this.state, this.buyboxCallbacks)) : undefined,
            inventoryLabel: isActive && isPriceAvailable && customerInfo ? getBuyBoxInventoryLabel(props) : undefined,
            shopSimilarLook: this.props.config.enableShopSimilarLooks && !product.IsGiftCard ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Looks) : undefined,
            shopSimilarDescription: this.props.config.enableShopSimilarDescription && !product.IsGiftCard ? getBuyboxShopSimilarButton(props, ShopSimiliarButtonType.Description) : undefined,
            keyInPrice: isActive && isPriceAvailable && this.props.config.enableKeyInPrice && this.state.isCustomPriceSelected ? getBuyboxKeyInPrice(props, this.state, this.buyboxCallbacks) : undefined,
            quantityLimitsMessages: getQuantityLimitsMessages(props, this.state),
            min: applyDefaultOrderSettings ? min : 1,
            max,
            priceState: customerInfo ? true : false,
            signInLink: this._getSigninButton(undefined, user, this.props.resources),
            // registerUserLink:this._getRequestanAccountButton(undefined,user,this.props.resources),
            OverSizedLabel: isActive && isPriceAvailable && isOversized ? (<div className="ms-buybox__oversized">This is an oversized item and will require higher shipping charge</div>) : undefined,
            invetLabel: inventLabel,
            etaLabel: isActive && isPriceAvailable ? etaResult : [],
            alternateItem: currentItemAlternate,
            productWarehouseDtls: isActive && isPriceAvailable && customerInfo ? getProductWarehouseDetails(props.data.productWarehouseDetails?.result,this.props.config.warehouseNoteText) : undefined,
            productDetailsTab: productSpecifications && productSpecifications.length > 0 ? productSpecifications[0]: undefined,
            IsActive: isActive && isPriceAvailable,
            IsAlternateItemCallFailed: isActive && isPriceAvailable && customerInfo && isAlternateItemCallFailed ?(<div className="ms-buybox__oversized">{this.props.config.alternateItemCallFailedMessageText}</div>):undefined,
            applyDefaultOrderSettings: applyDefaultOrderSettings,
            isChannelMultipleCatalogsFeatureEnabled: this._isChannelMultipleCatalogsFeatureEnabled(),
            currentCatalogId: catalogId,
            catalogs: this._validateCatalogDetails(catalogId)

        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private _getInventoryLabel = (productQty: IProductInventoryInformation[] | undefined) => {
        if (productQty) {
            const newShippingQuantity = productQty.find(shipping => shipping.deliveryType === DeliveryMode.shipping);
            if (newShippingQuantity) {
                return newShippingQuantity.StockLevelLabel;
            }
        }
        return '';
    };
    /**
     * Handles anchor click event and scrolls to the given bulk purchase module.
     * @param bulkPurchaseModule - Bulk purchase module.
     * @returns Handler.
     */
    private readonly _onBulkPurchaseLinkClick = (bulkPurchaseModule: Element) => () => {
        const boundingRect = bulkPurchaseModule.getBoundingClientRect();
        window.scrollTo(boundingRect.left, boundingRect.top);
    };

    private readonly _isChannelMultipleCatalogsFeatureEnabled = (): boolean => {
        let isMultipleCatalogFeatureEnabledInHq = false;
        if (this.props.data.featureState) {
            isMultipleCatalogFeatureEnabledInHq =
                this.props.data.featureState.result?.find(
                    feature => feature.Name === 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature'
                )?.IsEnabled || false;
        }
        return isMultipleCatalogFeatureEnabledInHq;
    };

    private _renderBuyboxBulkPurchaseLink(): React.ReactNode {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Temporary switch for the feature which is under development.
        if (!this.props.context.app.config.isBulkPurchaseEnabled) {
            return null;
        }

        if (!msdyn365Commerce.isBrowser) {
            return null;
        }

        const bulkPurchaseModules = Array.from(document.getElementsByClassName(BulkPurchase.className));

        if (!ArrayExtensions.hasElements(bulkPurchaseModules)) {
            return null;
        }

        return (
            <Button
                className='ms-buybox__bulk-purchase-anchor'
                title={this.props.resources.bulkPurchaseLinkText}
                onClick={this._onBulkPurchaseLinkClick(bulkPurchaseModules[0])}
            >
                {this.props.resources.bulkPurchaseLinkText}
            </Button>
        );
    }

    private _updatePrice(
        newPrice: ProductPrice | undefined,
        customPrice: number | undefined = this.state.keyInPriceAmount): void {

        if (this.state.isCustomPriceSelected && newPrice) {
            newPrice.CustomerContextualPrice = customPrice;
        }
        this.setState({ productPrice: newPrice });
    }

    private _getProductVariant(dimensionsToUpdate: { [id: number]: string }) {
        const {
            data: {
                product: { result: product }
            },
            context: {
                actionContext,
                request: {
                    apiSettings: { channelId }
                }
            }
        } = this.props;

        const productDimensions = this.props.data.productDimensions.result!;

        // Step 3, Build the actually selected dimensions, prioritizing the information in state
        // over the information in data
        const mappedDimensions = productDimensions
            .map(dimension => {
                const dimensions = (dimension.dimensionValuesWithInventory ??
                    dimension.DimensionValues ??
                    []) as IDimensionValueForSelectedVariant[];
                return {
                    DimensionTypeValue: dimension.DimensionTypeValue,
                    DimensionValue:
                        dimensions.find(
                            value => value.DimensionValue?.RecordId.toString() === dimensionsToUpdate[dimension.DimensionTypeValue]
                        )?.DimensionValue ?? dimension.DimensionValue,
                    ExtensionProperties: dimension.ExtensionProperties
                };
            })
            .filter(dimension => dimension.DimensionValue);
        setDimensionValuesToQuery(this.props.context.actionContext.requestContext.url.requestUrl, mappedDimensions);

        // Step 4. Use these dimensions hydrate the product. Wrap this in a promise
        // so that places like add to cart can await it
        const selectedProductPromise = getSelectedVariant(
            new SelectedVariantInput(
                product!.MasterProductId ? product!.MasterProductId : product!.RecordId,
                channelId,
                undefined,
                undefined,
                this.props.context.request
            ),
            actionContext
        );
        this.setState({ selectedProduct: selectedProductPromise });

        return { productVariantPromise: selectedProductPromise, mappedDimensions };
    }

    private readonly _updateDimensions = async (): Promise<void> => {
        const {
            data: {
                product: { result: product }
            }
        } = this.props;

        const productDimensions = this.props.data.productDimensions.result;

        const hasProductDimensions = ArrayExtensions.hasElements(productDimensions);

        if (!product || !hasProductDimensions) {
            return;
        }

        const dimensionsToUpdate: { [id: number]: string } = { ...this.dimensions };
        this.setState({ isUpdatingDimension: true, isUpdatingDeliveryOptions: true });

        // Step 1: Clear error state to display relevant errors
        if (this.state.errorState.otherError || this.state.errorState.quantityError) {
            const clearErrorState = { ...this.state.errorState };
            clearErrorState.otherError = undefined;
            if (this.state.errorState.errorHost === 'ADDTOCART') {
                clearErrorState.quantityError = undefined;
                clearErrorState.errorHost = undefined;
            }
            this.setState({ errorState: clearErrorState });
        }

        // Step 2: Clear any errors indicating the dimension wasn't selected
        for (const key of Object.keys(dimensionsToUpdate)) {
            if (this.state.errorState.configureErrors[`${key}`]) {
                const errorState = { ...this.state.errorState };
                errorState.configureErrors[`${key}`] = undefined;

                this.setState({ errorState });
            }
        }

        const data = this._getProductVariant(dimensionsToUpdate);
        const productVariant: SimpleProduct | null = await data.productVariantPromise;

        if (!productVariant) {
            return;
        }

        const promises: Promise<void>[] = [
            this._getDimensionsForSelectedVariant(productVariant, data.mappedDimensions),
            this._hydrateInventoryInfo(productVariant),
            this._hydratePrice(productVariant),
            this._hydrateDeliveryOptions(productVariant),
            this._updateQuantitiesInState(productVariant)
        ];

        await Promise.all(promises);
    };

  private async _getDimensionsForSelectedVariant(
        productVariant: SimpleProduct,
        mappedDimensions: {
            /* eslint-disable @typescript-eslint/naming-convention -- Retail proxy values */
            DimensionTypeValue: number;
            DimensionValue: ProductDimensionValue | undefined;
            ExtensionProperties: CommerceProperty[] | undefined;
            /* eslint-enable @typescript-eslint/naming-convention -- Retail proxy values */
        }[]
    ): Promise<void> {
        await getDimensionsForSelectedVariant(
            new GetDimensionsForSelectedVariantInput(
                productVariant.MasterProductId ? productVariant.MasterProductId : productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                mappedDimensions,
                this.props.context.request
            ),
            this.props.context.actionContext
        );
    }

    private async _hydrateInventoryInfo(productVariant: SimpleProduct): Promise<void> {
        // Step 5. Use these dimensions hydrate the inventory. Wrap this in a promise
        // so that places like add to cart can await it
        const newAvailableQuantity = await getProductAvailabilitiesForSelectedVariant(
            new ProductAvailabilitiesForSelectedVariantInput(
                productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                productVariant
            ),
            this.props.context.actionContext
        );

        const newShippingQuantity = newAvailableQuantity?.find(
            // @ts-expect-error
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (shipping: { deliveryType: any }) => shipping.deliveryType === DeliveryMode.shipping
        );
        const isCustomPriceSelected = productVariant.Dimensions?.find(
            dimension =>
                dimension.DimensionTypeValue === 4 && dimension.DimensionValue && dimension.DimensionValue.Value?.toLowerCase() === 'custom'
        );
        if (isCustomPriceSelected) {
            this.setState({ isCustomPriceSelected: true });
        } else {
            this.setState(previousState => {
                const errorState = previousState.errorState;
                errorState.customAmountError = undefined;
                return { isCustomPriceSelected: false, isPriceKeyedIn: false, errorState };
            });
        }

        if (newShippingQuantity) {
            this.setState({ productAvailableQuantity: newShippingQuantity });
        } else {
            this.setState({ productAvailableQuantity: undefined });
        }
    }

    private async _hydratePrice(productVariant: SimpleProduct): Promise<void> {
        // This function is invoked when dimensionSelectedAsync get called.
        // If the price has not been hydrated before, this means the component was just loaded for the first time with variant selected.
        // We will skip calling RS API in this case as the price data has already been fetched via module data action in node server.
        if (!this.state.isPriceApiCalled) {
            this.setState({ isPriceApiCalled: true });
            return;
        }

        // Step 6. Use these dimensions hydrate the product price.
        const newPrice = await getPriceForSelectedVariant(
            new PriceForSelectedVariantInput(productVariant.RecordId, this.props.context.request.apiSettings.channelId, productVariant),
            this.props.context.actionContext
        );

        if (newPrice) {
            this._updatePrice(newPrice);
        }
    }

    private async _hydrateDeliveryOptions(productVariant: SimpleProduct): Promise<void> {
        const retailMultiplePickupFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
        );

        // Step 7. Use these dimensions hydrate the product delivery options.
        const newDeliveryOptions = await getDeliveryOptionsForSelectedVariant(
            new GetDeliveryOptionsForSelectedVariantInput(
                productVariant.RecordId,
                this.props.context.request.apiSettings.channelId,
                productVariant,
                undefined,
                retailMultiplePickupFeatureState?.IsEnabled
            ),
            this.props.context.actionContext
        );

        if (newDeliveryOptions) {
            this.setState({ productDeliveryOptions: newDeliveryOptions });
        }
    }

    private async _updateQuantitiesInState(product: SimpleProduct): Promise<void> {
    const isOrderQuantityLimitsFeatureEnabled = await this._isOrderQuantityLimitsFeatureEnabled();
    const isChannelLimitsFeatureEnabled = await this._isChannelLimitsFeatureEnabled();

    const min: number = this._getMinQuantityForCartLineItem(isOrderQuantityLimitsFeatureEnabled, product);
    const max: number = this._getMaxQuantityForCartLineItem(
        isChannelLimitsFeatureEnabled || isOrderQuantityLimitsFeatureEnabled,
        product
    );
    let quantity: number = this._getQuantity(product, isOrderQuantityLimitsFeatureEnabled);
    quantity = Math.max(quantity, min);

    this.setState({
        min,
        max,
        quantity
    });
    }

     private _getMinQuantityForCartLineItem(isOrderQuantityLimitsFeatureEnabled: boolean, product: SimpleProduct): number {
     if (
         isOrderQuantityLimitsFeatureEnabled &&
         this._getIgnoreOrderSettings() &&
         product?.Behavior?.MinimumQuantity &&
         product.Behavior.MinimumQuantity > 0
     ) {
         return product.Behavior.MinimumQuantity;
     }

     return 1;
 }

 private _getMaxQuantityForCartLineItem(isQuantityLimitsFeatureEnabled: boolean, product: SimpleProduct): number {
     if (
         isQuantityLimitsFeatureEnabled &&
         this._getIgnoreOrderSettings() &&
         product?.Behavior?.MaximumQuantity &&
         product.Behavior.MaximumQuantity > 0
     ) {
         return product.Behavior.MaximumQuantity;
     }

     return updateMaxQuantityForCartLineItem(this.props.context.app.config.maxQuantityForCartLineItem);
 }

 private _getQuantity(product: SimpleProduct, isOrderQuantityLimitsFeatureEnabled: boolean): number {
    const { quantity } = this.state;
    let defaultQuantity = 1;

    if (quantity !== defaultQuantity || !isOrderQuantityLimitsFeatureEnabled) {
        return quantity;
    }

    const cartConfiguration = this.props.data.cartConfiguration;
    const applyDefaultOrderSettingsForCartLine = cartConfiguration?.result?.ValidateDefaultOrderSettingsPerLine ?? false;
    if (!applyDefaultOrderSettingsForCartLine) {
        return quantity;
    }

       if (product?.Behavior?.DefaultQuantity && product.Behavior.DefaultQuantity > 0) {
            defaultQuantity = product.Behavior.DefaultQuantity;
        }

        return defaultQuantity;
    }

  private _getIgnoreOrderSettings(): boolean {
        const cartConfiguration = this.props.data.cartConfiguration;
        return !(cartConfiguration?.result?.IgnoreOrderSettings ?? false);
    }

    private readonly _getDropdownName = (dimensionType: number, resources: IBuyboxResources): string => {
        const isGiftCard = this.props.data.product.result?.IsGiftCard;

        switch (dimensionType) {
            case 1: // ProductDimensionType.Color
                return resources.productDimensionTypeColor;
            case 2: // ProductDimensionType.Configuration
                return resources.productDimensionTypeConfiguration;
            case 3: // ProductDimensionType.Size
                return resources.productDimensionTypeSize;
            case 4: // ProductDimensionType.Style
                return isGiftCard ? resources.productDimensionTypeAmount : resources.productDimensionTypeStyle;
            default:
                return '';
        }
    };

    private async _isOrderQuantityLimitsFeatureEnabled(): Promise<boolean> {
        const featureStatuses = await this.props.data.featureState;

        const isFeatureEnabledInHq = featureStatuses?.find(
            featureState => featureState.Name === RetailDefaultOrderQuantityLimitsFeatureName
        )?.IsEnabled;
        if (!isFeatureEnabledInHq) {
            return false;
        }
        const useSiteBuilderSettings = featureStatuses?.find(
            featureState => featureState.Name === RevertToSiteBuilderOrderQuantityLimitsSettingsCRTFeatureName
        )?.IsEnabled;

        if (useSiteBuilderSettings) {
            const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
            if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
                return false;
            }

            if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
                return true;
            }
            let customerInfo;
            try {
                customerInfo = await this.props.data.customerInformation;
            } catch (error) {
                // @ts-ignore
                this.props.telemetry.information(error);
                this.props.telemetry.debug('Unable to receive Customer Information. May be user is not authorized');
                return false;
            }

            return (
                customerInfo &&
                ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                    (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
            );
        } else {
            const cartConfiguration = await this.props.data.cartConfiguration;
            return !(cartConfiguration?.IgnoreOrderSettings ?? false);
        }
    }

    private _validateCatalogDetails(catalogId: number): ProductCatalog[] | undefined {
        const catalogs = this.props.data.catalogs?.result?.filter(item => item.RecordId === catalogId);
        if (ArrayExtensions.hasElements(catalogs) && catalogs[0].RecordId === 0) {
            return;
        }

        return catalogs;
    }

    private async _isChannelLimitsFeatureEnabled(): Promise<boolean> {
        const featureStatuses = await this.props.data.featureState;

        const isChannelLimitsFeatureEnabledInHq = featureStatuses?.find(
            featureState => featureState.Name === EcommerceQuantityLimitConfigurationsFeatureName
        )?.IsEnabled;

        return isChannelLimitsFeatureEnabledInHq ?? false;
    }
}

export default Buybox;
